import { StopKind, Transport, TransportSide } from '@/new-transport/_models/transport.model';
import {
	GroupedTimetableByVehicle,
	TransportLogic,
	TransportTimetable,
	TransportVehicleKind,
} from '@/new-transport/_models/transport-timetable.model';
import { removeDuplicatesFromArray } from '@/_utils/remove-duplicates-from-array';

export class TransportTimetableProcessor {
	/**
	 * Groups and sorts timetable data by vehicle, linking transport logic, timetable entries,
	 * and stops into structured objects, each associated with a vehicle. The function also
	 * handles the assignment of antennas for vehicles and ensures sorting of both transport
	 * logic and timetable entries by their order property.
	 *
	 * @param {TransportTimetable[]} timetable - Array of timetable entries, each associated with a stop.
	 * @param {TransportLogic[]} transportLogic - Array of transport logic, defining the movement of vehicles between stops.
	 * @param {Transport} transport - Object containing transport configuration, such as side (DEPARTURE/RETURN) and terms (departureTerms/returnTerms).
	 * @returns {GroupedTimetableByVehicle[]} - Array of grouped timetable data, each entry associated with a vehicle.
	 */
	groupAndSortTimetableData(
		timetable: TransportTimetable[],
		transportLogic: TransportLogic[],
		transport: Transport,
	): GroupedTimetableByVehicle[] {
		return transportLogic.reduce<GroupedTimetableByVehicle[]>((acc, logic) => {
			const timetableEntry = this.findById(timetable, logic.city, 'stop.id');

			if (!logic.vehicle || !timetableEntry) {
				return [];
			}

			let vehicleEntry = acc.find((entry) => entry.vehicle.id === logic.vehicle.id);

			if (!vehicleEntry) {
				vehicleEntry = {
					vehicle: { ...logic.vehicle, transportVehicleKind: TransportVehicleKind.ANTENNA },
					timetable: [],
					transportLogic: [],
					antennasForVehicle: [],
				} as GroupedTimetableByVehicle;
				acc.push(vehicleEntry);
			}
			vehicleEntry.transportLogic.push(logic);

			const antennas = this.getAntennasFromLogic(transportLogic, logic);

			vehicleEntry.antennasForVehicle.push(...antennas);
			vehicleEntry.antennasForVehicle = removeDuplicatesFromArray(vehicleEntry.antennasForVehicle);

			this.addTimetableEntry(vehicleEntry, timetableEntry);

			vehicleEntry.transportLogic.sort((a, b) => a.order - b.order).slice();
			vehicleEntry.timetable
				.sort((a, b) => {
					const orderA = vehicleEntry.transportLogic.find((item) => item.city === a.stop.id)?.order || 0;
					const orderB = vehicleEntry.transportLogic.find((item) => item.city === b.stop.id)?.order || 0;
					return transport?.side === TransportSide.DEPARTURE ? orderA - orderB : orderA + orderB;
				})
				.slice();

			// The vehicle should be a BUS only when the last transportLogic in vehicleEntry is StopKind.TARGET,
			// but because sometimes stops are added manually in the database, the transportLogic order may differ.
			// Temporarily, we check whether vehicleEntry contains StopKind.TARGET at all,
			// and we need to fix adding new stops so that they are in the right order
			if (vehicleEntry.transportLogic.some((v) => v.action === StopKind.TARGET)) {
				vehicleEntry.vehicle.transportVehicleKind = TransportVehicleKind.BUS;
			}

			return acc;
		}, []);
	}

	/**
	 * Finds an object in an array by a specific key and its value.
	 *
	 * @template T
	 * @param {T[]} array - The array of objects to search through.
	 * @param {number} id - The value to search for, typically the ID.
	 * @param {keyof T} keyPath - The key in the object to compare the `id` with.
	 * @returns {T | undefined} - The found object or undefined if no match is found.
	 */
	private findById<T>(array: T[], id: number, keyPath: string): T | undefined {
		return array.find((item) => this.getNestedValue(item, keyPath) === id);
	}

	/**
	 * Retrieves a nested value from an object based on a dot-separated key path.
	 *
	 * @template T - The type of the object and the return value.
	 * @param {T} obj - The object from which to retrieve the value.
	 * @param {string} keyPath - The dot-separated string representing the key path (e.g., "user.name.first").
	 * @returns {T | undefined} - Returns the nested value if found, otherwise returns undefined.
	 *
	 * @example
	 * // Example usage:
	 * const obj = { user: { name: { first: 'John', last: 'Doe' } } };
	 * const firstName = getNestedValue(obj, 'user.name.first'); // Returns 'John'
	 * const lastName = getNestedValue(obj, 'user.name.last'); // Returns 'Doe'
	 * const age = getNestedValue(obj, 'user.age'); // Returns undefined
	 */
	private getNestedValue<T>(obj: T, keyPath: string): T | undefined {
		return keyPath.split('.').reduce((acc, key) => acc && acc[key], obj);
	}

	/**
	 * Adds a timetable entry to the specified vehicle's timetable if the stop exists.
	 * Removes any duplicate entries after adding the timetable entry.
	 *
	 * @private
	 * @param {GroupedTimetableByVehicle} vehicleEntry - The vehicle's timetable grouping where the entry will be added.
	 * @param {TransportTimetable} timetableEntry - The timetable entry containing details such as stop, time, etc.
	 * @returns {void}
	 */
	private addTimetableEntry(vehicleEntry: GroupedTimetableByVehicle, timetableEntry: TransportTimetable): void {
		vehicleEntry.timetable.push(timetableEntry);
		vehicleEntry.timetable = removeDuplicatesFromArray(vehicleEntry.timetable);
	}

	/**
	 * Determines whether an antenna should be added based on changes in transport logic.
	 *
	 * This function compares the current and previous transport logic to decide
	 * whether an antenna should be added. The criteria for adding an antenna are:
	 * 1. The vehicle has changed.
	 * 2. The city, origin city, and transport type remain the same.
	 * 3. The current action is `BOARDING`, and the previous action was `DROP_OFF`.
	 *
	 * @param {TransportLogic} previousLogic - The transport logic from the previous state.
	 * @param {TransportLogic} currentLogic - The transport logic from the current state.
	 * @returns {boolean} - Returns `true` if an antenna should be added, otherwise `false`.
	 */
	private shouldAddAntenna(previousLogic: TransportLogic, currentLogic: TransportLogic): boolean {
		return (
			previousLogic.vehicle.id !== currentLogic.vehicle.id &&
			previousLogic.city === currentLogic.city &&
			previousLogic.originCity === currentLogic.originCity &&
			previousLogic.transport === currentLogic.transport &&
			currentLogic.action === StopKind.BOARDING &&
			previousLogic.action === StopKind.DROP_OFF
		);
	}

	/**
	 * Retrieves the antennas from the provided transport logic array based on the given logic.
	 * Filters through the array and adds an antenna if the condition in `shouldAddAntenna` is met.
	 *
	 * @private
	 * @param {TransportLogic[]} transportLogicArr - The array of transport logic objects to be filtered.
	 * @param {TransportLogic} logic - The current logic to compare against each item in the array.
	 * @returns {{ vehicle: number; city: number }[]} - An array of objects containing vehicle and city information.
	 */
	private getAntennasFromLogic(
		transportLogicArr: TransportLogic[],
		logic: TransportLogic,
	): { vehicle: number; city: number }[] {
		return transportLogicArr
			.filter((previousLogic) => this.shouldAddAntenna(previousLogic, logic))
			.map((previousLogic) => ({ vehicle: previousLogic.vehicle.id, city: previousLogic.city }));
	}
}
